/**
 * Uygulama kabuğu — MOBİLDE ALT SEKME, MASAÜSTÜNDE KENAR ÇUBUĞU.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * TEK ROTA AĞACI, İKİ DÜZEN [K-020].
 *
 * Aynı gezinme öğeleri iki farklı yerleşimde çizilir; mobil ve masaüstü için
 * AYRI ekranlar yazılmaz. Ayrı yazılsaydı ikisi zamanla ayrışır ve bir
 * ekranda düzeltilen hata diğerinde kalırdı — bu projede tekrarlayan
 * "tek doğruluk kaynağı" ilkesinin arayüzdeki hâli.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Alt sekme çubuğu `env(safe-area-inset-bottom)` kadar aşağıdan boşluk alır:
 * iPhone'un alt çizgisi düğmeleri yutmasın.
 */
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { PwaDurumu } from '../pwa/Durum.tsx';
import { cx } from '../design/cx.ts';

interface NavItem {
  readonly to: string;
  readonly label: string;
  readonly icon: ReactNode;
}

/**
 * Gezinme öğeleri — TEK tanım, iki düzende kullanılıyor.
 *
 * Simgeler satır içi SVG: ikon kütüphanesi bağımlılığı eklemiyoruz. Dört
 * simge için bir paket çekmek, sonra o paketin sürüm yükseltmesinde görsel
 * kırılma riski taşımak orantısız.
 */
const NAV: readonly NavItem[] = [
  {
    to: '/',
    label: 'Sahalar',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="size-6">
        <path
          d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    to: '/harita',
    label: 'Harita',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="size-6">
        <path
          d="M9 3 3 6v15l6-3 6 3 6-3V3l-6 3-6-3ZM9 3v15M15 6v15"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    to: '/ara',
    label: 'Ara',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="size-6">
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    to: '/profil',
    label: 'Profil',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="size-6">
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21c0-4 3.6-6 8-6s8 2 8 6" strokeLinecap="round" />
      </svg>
    ),
  },
];

/**
 * Yapılandırmanın üç listesi TEK gezinme öğesine bağlıdır.
 *
 * `/panolar` ve `/tanklar` kendi alt sekmelerinde yaşıyor (bkz.
 * `field/parts.tsx`), ama alt çubukta ayrı birer öğe DEĞİLLER: mobilde dört
 * öğe zaten sınırda: altı öğe dokunma hedeflerini 44 px'in altına indirirdi.
 * Kullanıcı panolardayken "Sahalar" öğesi vurgulu kalır — yoksa kabuk hiçbir
 * yerde değilmiş gibi görünür.
 */
const GRUP: Record<string, string> = {
  '/sahalar': '/',
  '/panolar': '/',
  '/tanklar': '/',
  '/denetim': '/',
  '/paylasim': '/',
};

function useAktif(): (to: string) => boolean {
  const yol = useRouterState({ select: (s) => s.location.pathname });
  return (to) => {
    if (yol === to || yol.startsWith(`${to}/`)) return true;
    const kok = `/${yol.split('/')[1] ?? ''}`;
    return GRUP[kok] === to;
  };
}

export function AppShell(): React.JSX.Element {
  const aktif = useAktif();

  return (
    <div className="min-h-dvh md:flex">
      {/* ── Masaüstü: kenar çubuğu ─────────────────────────────────────── */}
      <nav
        aria-label="Ana gezinme"
        className="hidden w-56 shrink-0 border-r border-border bg-surface md:flex md:flex-col"
      >
        <div className="px-4 py-5">
          <p className="font-semibold text-ink">Gasco SCADA</p>
        </div>
        <ul className="flex flex-col gap-1 px-2">
          {NAV.map((item) => (
            <li key={item.to}>
              <Link
                to={item.to}
                aria-current={aktif(item.to) ? 'page' : undefined}
                className={cx(
                  'flex min-h-11 items-center gap-3 rounded-[var(--radius-control)] px-3 text-sm font-medium',
                  aktif(item.to)
                    ? 'bg-brand-50 text-brand-700'
                    : 'text-ink-muted hover:bg-surface-sunken hover:text-ink',
                )}
              >
                {item.icon}
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      {/* ── İçerik ─────────────────────────────────────────────────────── */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Çevrimdışı ve yeni sürüm uyarıları içeriğin ÜSTÜNDE: kullanıcı
            ekrandaki verinin durumunu okumadan önce görsün. */}
        <PwaDurumu />
        <main className="flex-1 pb-20 md:pb-0">
          <Outlet />
        </main>
      </div>

      {/* ── Mobil: alt sekme çubuğu ────────────────────────────────────── */}
      <nav
        aria-label="Ana gezinme"
        className="fixed inset-x-0 bottom-0 z-10 border-t border-border bg-surface pb-[env(safe-area-inset-bottom)] md:hidden"
      >
        <ul className="flex">
          {NAV.map((item) => (
            <li key={item.to} className="flex-1">
              <Link
                to={item.to}
                aria-current={aktif(item.to) ? 'page' : undefined}
                // min-h-14: dokunma hedefi 44 px'in üstünde kalsın.
                className={cx(
                  'flex min-h-14 flex-col items-center justify-center gap-0.5 text-xs font-medium',
                  aktif(item.to) ? 'text-brand-600' : 'text-ink-muted',
                )}
              >
                {item.icon}
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}

/**
 * Sayfa başlığı — her ekranda aynı hizada, aynı boşlukta.
 *
 * Ekran başına elle yazılsaydı biri `p-4` biri `p-6` kullanır ve sayfalar
 * arası geçişte içerik zıplardı.
 */
export function PageHeader({
  title,
  description,
  action,
}: {
  readonly title: string;
  readonly description?: string;
  readonly action?: ReactNode;
}): React.JSX.Element {
  return (
    <header className="flex items-start justify-between gap-3 border-b border-border bg-surface px-4 py-4">
      <div className="min-w-0">
        <h1 className="truncate text-xl font-semibold text-ink">{title}</h1>
        {description !== undefined && (
          <p className="mt-0.5 text-sm text-ink-muted">{description}</p>
        )}
      </div>
      {action}
    </header>
  );
}

export function PageBody({ children }: { readonly children: ReactNode }): React.JSX.Element {
  return <div className="p-4">{children}</div>;
}
